import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import { isUserOnTicket, fmtRelative, fmtDaysUntil, moneyFmt } from '../lib/tickets.js';
import { TicketStatusBadge, TicketPriorityBadge, RenewalTierBadge, AssigneeCell } from '../components/TicketBits.jsx';

const WINDOW_DAYS = 14;
const ACTIVE_RENEWAL_CAP = 25; // guard against a flood of stale past-dues

function isoDay(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
function dayWindow(now, days = WINDOW_DAYS) {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return Array.from({ length: days }, (_, i) => isoDay(new Date(start.getTime() + i * 86400000)));
}
function formatDayHeader(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-CA',
    { weekday: 'short', month: 'short', day: '2-digit', timeZone: 'UTC' }).toUpperCase();
}
function isWeekend(iso) {
  const d = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
}

export default function Schedule() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [renewals, setRenewals] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all'); // all | renewals | tickets
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // all | me | unassigned

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, t] = await Promise.all([
          axios.get('/api/schedule/renewals'),
          axios.get('/api/tickets')
        ]);
        if (cancelled) return;
        setRenewals(r.data.renewals || []);
        setTickets(t.data.data || []);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const now = new Date();
  const windowDays = dayWindow(now);
  const todayISO = windowDays[0];
  const windowSet = useMemo(() => new Set(windowDays), [windowDays.join()]);

  const showRenewals = typeFilter !== 'tickets';
  const showTickets = typeFilter !== 'renewals';

  // Tickets passing the assignee filter (open only for the schedule).
  const openTickets = useMemo(() => {
    let list = tickets.filter((t) => t.status !== 'closed');
    if (assigneeFilter === 'me') list = list.filter((t) => isUserOnTicket(t, user?.id));
    if (assigneeFilter === 'unassigned') list = list.filter((t) => t.primaryAssigneeId == null && t.collaborators.length === 0);
    return list;
  }, [tickets, assigneeFilter, user]);

  // ── Active bucket ──────────────────────────────────────────────
  const pastDue = renewals.filter((r) => r.tier === 'past-due');
  const myOpen = tickets.filter((t) => t.status !== 'closed' && isUserOnTicket(t, user?.id));
  const highPri = openTickets.filter((t) => t.priority === 'high');
  const renewalsDue = renewals.filter((r) => windowSet.has(r.nextChargeDate));

  const activeTickets = useMemo(() => {
    if (!showTickets) return [];
    const seen = new Set();
    const out = [];
    for (const t of [...highPri, ...myOpen]) {
      if (seen.has(t.id)) continue; seen.add(t.id); out.push(t);
    }
    return out;
  }, [highPri, myOpen, showTickets]);

  // ── Timeline buckets ───────────────────────────────────────────
  const timeline = useMemo(() => {
    const idx = new Map(windowDays.map((d) => [d, []]));
    if (showRenewals) {
      for (const r of renewals) {
        const b = idx.get(r.nextChargeDate);
        if (b) b.push({ kind: 'renewal', renewal: r });
      }
    }
    if (showTickets) {
      for (const t of openTickets) {
        const created = isoDay(new Date(t.createdAt));
        const updated = isoDay(new Date(t.updatedAt));
        if (windowSet.has(updated) && updated !== created) idx.get(updated).push({ kind: 'ticket', ticket: t, event: 'updated' });
        else if (windowSet.has(created)) idx.get(created).push({ kind: 'ticket', ticket: t, event: 'created' });
      }
    }
    return idx;
  }, [renewals, openTickets, showRenewals, showTickets, windowDays.join(), windowSet]);

  const totalDue = renewalsDue.reduce((s, r) => s + (Number(r.annual) || 0), 0);

  function goRenewal(r) { navigate(`/customers`); }
  function goTicket(t) { navigate(`/tickets/${t.id}`); }

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Schedule</h2>
          <p className="page-sub">Combined view of renewals and active tickets for the next two weeks. Click anything to open it.</p>
        </div>
      </div>

      <div className="kpi-strip">
        <div className={`kpi-tile ${renewalsDue.length > 0 ? 'kpi-warn' : ''}`}>
          <div className="kpi-value">{renewalsDue.length}</div>
          <div className="kpi-label">Renewals due (14d)</div>
          <div className="kpi-trend cell-mono">{moneyFmt(totalDue)}</div>
        </div>
        <div className={`kpi-tile ${openTickets.length > 0 ? 'kpi-warn' : ''}`}>
          <div className="kpi-value">{tickets.filter((t) => t.status !== 'closed').length}</div>
          <div className="kpi-label">Tickets active</div>
        </div>
        <div className={`kpi-tile ${myOpen.length > 0 ? 'kpi-warn' : 'kpi-good'}`}>
          <div className="kpi-value">{myOpen.length}</div>
          <div className="kpi-label">Assigned to me</div>
        </div>
        <div className={`kpi-tile ${highPri.length > 0 ? 'kpi-bad' : ''}`}>
          <div className="kpi-value">{highPri.length}</div>
          <div className="kpi-label">High priority</div>
        </div>
      </div>

      <div className="toolbar">
        <select className="layout-dropdown" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">Renewals + Tickets</option>
          <option value="renewals">Renewals only</option>
          <option value="tickets">Tickets only</option>
        </select>
        <select className="layout-dropdown" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
          <option value="all">All assignees</option>
          <option value="me">Me</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <span className="result-count cell-mono">Next 14 days</span>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="modal-sub">Loading schedule…</p>}

      {/* TODAY / ACTIVE */}
      {!loading && (activeTickets.length > 0 || (showRenewals && pastDue.length > 0)) && (
        <section className="active-section">
          <div className="active-head">
            <span className="active-title">Today / Active</span>
            <span className="cell-mono cell-muted">{activeTickets.length + (showRenewals ? Math.min(pastDue.length, ACTIVE_RENEWAL_CAP) : 0)} items</span>
          </div>
          <div className="active-list">
            {activeTickets.map((t) => (
              <button key={`t${t.id}`} type="button" className="active-row" onClick={() => goTicket(t)}>
                <TicketStatusBadge value={t.status} />
                <span className="active-row-title">{t.title}</span>
                <span className="cell-mono cell-muted active-row-sub">
                  {(t.siteName || 'Internal')} · updated {fmtRelative(t.updatedAt, now)}
                </span>
                {t.priority !== 'normal' && <TicketPriorityBadge value={t.priority} />}
                <AssigneeCell ticket={t} />
              </button>
            ))}
            {showRenewals && pastDue.slice(0, ACTIVE_RENEWAL_CAP).map((r) => (
              <button key={`r${r.id}`} type="button" className="active-row" onClick={() => goRenewal(r)}>
                <RenewalTierBadge tier={r.tier} />
                <span className="active-row-title">{r.siteName} — {r.moduleLabel}</span>
                <span className="cell-mono cell-muted active-row-sub">{r.siteNumber || r.company} · {fmtDaysUntil(r.daysUntil)}</span>
                <span className="cell-mono active-row-money">{moneyFmt(r.annual)}</span>
              </button>
            ))}
            {showRenewals && pastDue.length > ACTIVE_RENEWAL_CAP && (
              <div className="active-more cell-mono cell-muted">+{pastDue.length - ACTIVE_RENEWAL_CAP} more past-due renewals</div>
            )}
          </div>
        </section>
      )}

      {/* 14-DAY TIMELINE */}
      {!loading && (
        <div className="timeline">
          {windowDays.map((iso) => {
            const items = timeline.get(iso) || [];
            const isToday = iso === todayISO;
            const weekend = isWeekend(iso);
            return (
              <div key={iso} className={`day-block ${isToday ? 'day-today' : weekend ? 'day-weekend' : ''}`}>
                <div className="day-head">
                  <span className="day-label">{formatDayHeader(iso)}{isToday ? ' — today' : weekend ? ' · weekend' : ''}</span>
                  <span className="cell-mono cell-muted">{items.length === 0 ? '—' : `${items.length} item${items.length === 1 ? '' : 's'}`}</span>
                </div>
                {items.length === 0 ? (
                  <div className="day-empty cell-mono cell-muted">(nothing scheduled)</div>
                ) : (
                  <div className="day-items">
                    {items.map((it, i) => it.kind === 'renewal' ? (
                      <button key={`r${i}`} type="button" className="day-row day-row-renewal" onClick={() => goRenewal(it.renewal)}>
                        <span className="day-tag cell-mono">Renewal</span>
                        <span className="day-row-title">{it.renewal.siteName} · {it.renewal.moduleLabel}</span>
                        <RenewalTierBadge tier={it.renewal.tier} />
                        <span className="cell-mono day-row-money">{moneyFmt(it.renewal.annual)}</span>
                      </button>
                    ) : (
                      <button key={`t${i}`} type="button" className="day-row day-row-ticket" onClick={() => goTicket(it.ticket)}>
                        <span className="day-tag cell-mono">Ticket {it.event}</span>
                        <TicketStatusBadge value={it.ticket.status} />
                        <span className="day-row-title">{it.ticket.title}</span>
                        {it.ticket.priority !== 'normal' && <TicketPriorityBadge value={it.ticket.priority} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
